import React, { useContext, useMemo, useState } from "react";
import { useHiringRequests } from "../../contexts/HiringRequestsContext";
import { AuthContext } from "../../contexts/AuthContext";
import ReusableTable from "../../components/UI/ReusableTable";
import Notification from "../../components/UI/Notification";
import ConfirmationModal from "../../components/UI/ConfirmationModal";
import ReactPaginate from 'react-paginate';
import Drawer from "../../components/UI/Drawer";
import HiringRequestDetails from "./RequestDetails";

const ViewRequests = () => {
    const { pagination, requests, fetchHiringRequests, updateHiringRequest, loading } = useHiringRequests();
    const { user } = useContext(AuthContext);
    const [currentPage, setCurrentPage] = useState(1);
    const [notification, setNotification] = useState(null);
    const [modal, setModal] = useState({ isOpen: false, action: null, id: null });
    const [isFiltering, setIsFiltering] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedHiringRequest, setSelectedHiringRequest] = useState(null);

    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    const openDrawerForDetails = (hiringRequest) => {
        setSelectedHiringRequest(hiringRequest);
        setIsDrawerOpen(true);
    };

    const assignedRequests = useMemo(() => {
        return requests.filter((request) => request.actioned_by === user?.id);
    }, [requests, user]);

    // console.log("Assigned Requests:", assignedRequests);

    const requestsWithRemainingPositions = useMemo(() => {
        return assignedRequests.map((request) => ({
            ...request,
            remaining_positions: request.number_of_positions - (request.filled_positions || 0),
        }));
    }, [assignedRequests]);

    // console.log("Requests with Remaining Positions:", requestsWithRemainingPositions);

    const handleApprove = (id) => {
        setModal({ isOpen: true, action: "approve", id });
    };

    const handleReject = (id) => {
        setModal({ isOpen: true, action: "reject", id });
    };

    const confirmAction = () => {
        if (modal.action === "approve") {
          updateHiringRequest({ id: modal.id, status: "Approved", actioned_by: user?.id });
            setNotification({
            message: "Request approved successfully.",
            type: "success",
            });
        } else if (modal.action === "reject") {
          updateHiringRequest({ id: modal.id, status: "Rejected", actioned_by: user?.id });
          setNotification({
            message: "Request rejected successfully.",
            type: "success",
            });
        }
        setModal({ isOpen: false, action: null, id: null });
    };

    const columns = useMemo(
        () => [
            { label: "Position", field: "position" },
            { label: "Skills Required", field: "skills_required" },
            { label: "No. of Positions", field: "number_of_positions" },
            { label: "Remaining Positions", field: "remaining_positions" },
            { label: "Deadline", field: "deadline" },
            { label: "Status", field: "status" },
            {
                label: "Actions",
                field: "actions",
                render: (row) => (
                    <div className="flex items-center justify-center gap-3">
                        <button
                        onClick={() => openDrawerForDetails(row)}
                        className="text-blue-600 hover:text-blue-800 hover:underline hover:underline-offset-4"
                        title="View"
                        >
                            View
                        </button>
                        {row.status === "Pending" ? (
                            <>
                                <button
                                    onClick={() => handleApprove(row.id)}
                                    className="px-3 py-1 dark:bg-black dark:border dark:border-green-600 bg-green-600 text-white rounded-md hover:bg-green-700"
                                >
                                    Approve
                                </button>
                                <button
                                    onClick={() => handleReject(row.id)}
                                    className="px-3 py-1 dark:bg-black dark:border dark:border-red-600 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Reject
                                </button>
                            </>
                        ) : ""
                        }
                    </div>
                ),
            },
        ],
        [handleApprove, handleReject]
    );

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
          setCurrentPage(newPage);
          fetchHiringRequests(newPage);
        }
    };
      
    return (
        <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">

            {/* notification */}
            {notification && (
                <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
            )}

            <h2 className="md:text-2xl text-xl  font-semibold mb-6 dark:text-white">Assigned Hiring Requests</h2>

            <div>
                <ReusableTable 
                    data={requestsWithRemainingPositions} 
                    columns={columns} 
                    filterable 
                    loading={loading} 
                    onFilterChange={(filtering) => setIsFiltering(filtering)}
                />
                
                {!isFiltering && requestsWithRemainingPositions?.length > 0 && (
                    <ReactPaginate
                        breakLabel="..."
                        onPageChange={(event) => handlePageChange(event.selected + 1)}
                        forcePage={currentPage - 1}
                        pageRangeDisplayed={5}
                        pageCount={pagination.totalPages}
                        renderOnZeroPageCount={null}
                        nextLabel="Next >"
                        previousLabel="< Prev"
                        previousClassName='dark:text-white'
                        nextClassName="dark:text-white"
                        containerClassName="flex items-center justify-center mt-4"
                        pageClassName="mx-1 px-3 py-1 border rounded dark:text-white"
                        activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700"
                    />
                )}
            </div>

            {/* confirmation modal */}
             <ConfirmationModal
                isOpen={modal.isOpen}
                onClose={() => setModal({ isOpen: false, action: null, id: null })}
                onConfirm={confirmAction}
                message={`Are you sure you want to ${modal.action} this request?`}
            />

            {/* Drawer for Hiring request Details */}
            <Drawer
                isOpen={isDrawerOpen}
                onClose={closeDrawer}
                border_radius="md:rounded-none">
                
                <HiringRequestDetails
                    hiringRequest={selectedHiringRequest}
                    hiring_request_id={selectedHiringRequest?.id}
                />
            </Drawer>
        </div>
    );
};

export default React.memo(ViewRequests);
